import { ThemeEvents } from '@theme/events';

/**
 * Add-to-cart error logger.
 *
 * Listens for the theme's bubbling `CartErrorEvent` and reports failed
 * add-to-cart attempts to the store owner using Shopify's native contact form
 * endpoint (no external service required). Throttled and capped client-side so
 * it can never flood the inbox or break the storefront.
 *
 * Activated only when `snippets/atc-error-logger.liquid` renders the
 * `[data-atc-logger][data-enabled="true"]` config node.
 */
(() => {
  const COOLDOWN_MS = 5 * 60 * 1000; // suppress identical errors within 5 minutes
  const DAILY_CAP = 25; // hard ceiling of reports per browser per day
  const STORAGE_KEY = 'memelolo:atc-error-log';

  /** @returns {HTMLElement | null} */
  const getConfig = () => document.querySelector('[data-atc-logger]');

  const todayKey = () => new Date().toISOString().slice(0, 10);

  /** @returns {{ day: string, count: number, sent: Record<string, number> }} */
  const readStore = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '');
      if (parsed && parsed.day === todayKey()) return parsed;
    } catch (_) {
      /* ignore */
    }
    return { day: todayKey(), count: 0, sent: {} };
  };

  /** @param {{ day: string, count: number, sent: Record<string, number> }} store */
  const writeStore = (store) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (_) {
      /* ignore */
    }
  };

  /** @param {string} signature */
  const shouldSend = (signature) => {
    const store = readStore();
    if (store.count >= DAILY_CAP) return false;
    const last = store.sent[signature] || 0;
    return Date.now() - last > COOLDOWN_MS;
  };

  /** @param {string} signature */
  const markSent = (signature) => {
    const store = readStore();
    store.count += 1;
    store.sent[signature] = Date.now();
    writeStore(store);
  };

  /**
   * Resolves the product form element that triggered the error.
   * @param {string | undefined} sourceId
   * @returns {HTMLElement | null}
   */
  const findForm = (sourceId) => {
    if (sourceId) {
      const byId = document.getElementById(sourceId);
      if (byId) {
        return byId instanceof HTMLFormElement ? byId : byId.querySelector('form') || byId;
      }
    }
    return null;
  };

  /**
   * @param {HTMLElement | null} form
   * @param {string} name
   * @returns {string}
   */
  const readField = (form, name) => {
    const el = /** @type {HTMLInputElement | null} */ (form?.querySelector(`[name="${name}"]`));
    return el?.value || '';
  };

  document.addEventListener(ThemeEvents.cartError, async (event) => {
    try {
      const config = getConfig();
      if (!config || config.dataset.enabled !== 'true') return;

      const detail = /** @type {{ sourceId?: string, data?: { message?: string, description?: string, errors?: unknown } }} */ (
        /** @type {CustomEvent} */ (event).detail || {}
      );
      const { message = '', description = '', errors = null } = detail.data || {};

      const form = findForm(detail.sourceId);
      const variantId = readField(form, 'id');
      const quantity = readField(form, 'quantity') || '1';
      const generationId = readField(form, 'properties[_memelolo_generation_id]') || '(none)';

      const signature = `${variantId}::${message}`.slice(0, 200);
      if (!shouldSend(signature)) return;

      // Did the item actually make it into the cart? (the "looks added but errored" case)
      let cart = null;
      try {
        cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      } catch (_) {
        /* cart fetch is best-effort */
      }

      const inCart = cart?.items
        ? cart.items.some((/** @type {{ variant_id: number }} */ item) => String(item.variant_id) === String(variantId))
        : 'unknown';

      const shopify = /** @type {Record<string, any>} */ (window.Shopify || {});

      const lines = [
        `MEMELOLO — Add-to-cart error report`,
        `Time: ${new Date().toISOString()} (local: ${new Date().toString()})`,
        ``,
        `Shopify message: ${message}`,
        `Description: ${description || '(none)'}`,
        `Errors: ${errors ? JSON.stringify(errors) : '(none)'}`,
        ``,
        `Variant ID: ${variantId || '(unknown)'}`,
        `Quantity attempted: ${quantity}`,
        `In cart after error: ${inCart}`,
        `Cart token: ${cart?.token || '(unknown)'}`,
        `Cart item count: ${cart?.item_count ?? '(unknown)'}`,
        ``,
        `Preview generation ID: ${generationId}`,
        ``,
        `Page: ${location.href}`,
        `Referrer: ${document.referrer || '(none)'}`,
        `Shop: ${shopify.shop || '(unknown)'}`,
        `Locale / country: ${shopify.locale || '?'} / ${shopify.country || '?'}`,
        `Online: ${navigator.onLine}`,
        `Viewport: ${window.innerWidth}x${window.innerHeight}`,
        `User agent: ${navigator.userAgent}`,
      ];

      if (cart?.items?.length) {
        lines.push(``, `Current cart lines:`);
        for (const item of cart.items) {
          lines.push(`  - ${item.variant_id} ${item.title} x${item.quantity}`);
        }
      }

      const payload = new URLSearchParams({
        form_type: 'contact',
        utf8: '✓',
        'contact[name]': 'ATC Error Logger',
        'contact[email]': config.dataset.senderEmail || '',
        'contact[body]': lines.join('\n'),
      });

      await fetch(config.dataset.contactUrl || '/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
        keepalive: true,
      });

      markSent(signature);
    } catch (_) {
      // Logging must never interfere with the storefront.
    }
  });
})();
